import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDateTime } from '../../lib/utils';
import { Search, History, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  const [filters, setFilters] = useState({
    entity_type: 'all',
    action: 'all',
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.action !== 'all') params.action = filters.action;
      
      const response = await adminAPI.getAuditLogs(params);
      setLogs(response.data);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (action.includes('update')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (action.includes('delete')) return 'bg-red-100 text-red-700 border-red-200';
    if (action.includes('approve')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (action.includes('reject')) return 'bg-red-100 text-red-700 border-red-200';
    if (action.includes('login')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (action.includes('export')) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return null;
    
    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([field, values]) => (
          <div key={field} className="text-sm">
            <span className="font-medium">{field}:</span>
            <div className="ml-4 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Old:</span>
                <span className="ml-1 text-red-600">{JSON.stringify(values.old)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">New:</span>
                <span className="ml-1 text-emerald-600">{JSON.stringify(values.new)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-audit-logs">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Audit Logs</h1>
        <p className="text-muted-foreground mt-1">
          Track all system actions and changes
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select 
              value={filters.entity_type} 
              onValueChange={(v) => setFilters({ ...filters, entity_type: v })}
            >
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="record">Record</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
                <SelectItem value="system_config">Config</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.action} 
              onValueChange={(v) => setFilters({ ...filters, action: v })}
            >
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="signup">Signup</SelectItem>
                <SelectItem value="create_record">Create Record</SelectItem>
                <SelectItem value="update_record">Update Record</SelectItem>
                <SelectItem value="delete_record">Delete Record</SelectItem>
                <SelectItem value="create_inspection">Create Inspection</SelectItem>
                <SelectItem value="approve_user">Approve User</SelectItem>
                <SelectItem value="reject_user">Reject User</SelectItem>
                <SelectItem value="export_report">Export Report</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30px]"></TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead className="hidden lg:table-cell">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow 
                        className="table-row-hover cursor-pointer"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <TableCell>
                          {log.changes && Object.keys(log.changes).length > 0 ? (
                            expandedLog === log.id ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.user_email}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getActionColor(log.action)}>
                            {log.action.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-muted-foreground">{log.entity_type}:</span>
                            <span className="font-mono text-xs ml-1">{log.entity_id.slice(0, 8)}...</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {log.reason || '-'}
                        </TableCell>
                      </TableRow>
                      {expandedLog === log.id && log.changes && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/30">
                            <div className="p-4">
                              <h4 className="font-medium mb-2">Changes</h4>
                              {formatChanges(log.changes)}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
